import React, {useState, useEffect, useRef} from 'react'
import {Row, Col, Block} from 'jsxstyle'
import {ReactComponent as ChevronDownIcon} from '../../common/assets/chevron-down-icon.svg'

interface CustomDropdownProps {
  options: string[]
  value: string
  onChange: (val: string) => void
  onClick?: () => void
  placeholder?: string
  width?: string
  hoverBackgroundColor?: string
  borderColor?: string
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  onClick,
  placeholder = 'Select an option',
  width = '180px',
  hoverBackgroundColor = '#F0F8FF',
  borderColor = '#1E90FF',
}) => {
  const [open, setOpen] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [animate, setAnimate] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setShowDropdown(true)
      const timeout = setTimeout(() => setAnimate(true), 10)
      return () => clearTimeout(timeout)
    } else {
      setAnimate(false)
      const timeout = setTimeout(() => setShowDropdown(false), 200)
      return () => clearTimeout(timeout)
    }
  }, [open])

  useEffect(() => {
    if (dropdownRef.current) {
      const contentHeight = dropdownRef.current.scrollHeight
      const containerHeight = dropdownRef.current.clientHeight
      setIsScrollable(contentHeight > containerHeight)
    }
  }, [options, showDropdown])

  const handleSelect = (val: string) => {
    onChange(val)
    setOpen(false)
  }

  return (
    <Col position="relative" width={width} overflow="visible">
      <Row
        alignItems="center"
        padding="8px 12px"
        border={`1px solid ${borderColor}`}
        borderRadius="6px"
        backgroundColor="#FAFAFA"
        cursor="pointer"
        userSelect="none"
        hoverBackgroundColor={hoverBackgroundColor}
        transition="background-color 0.2s ease"
        props={{
          onClick: () => {
            if (onClick) onClick()
            setOpen(!open)
          },
        }}
      >
        <Block flex="1 1 auto" marginRight="8px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" color={value ? '#333' : '#888'}>
          {value || placeholder}
        </Block>
        <ChevronDownIcon
          width={14}
          height={14}
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Row>

      {showDropdown && (
        <Col
          position="absolute"
          top="calc(100% + 4px)"
          left="0"
          width="100%"
          border="1px solid #E5E5E5"
          borderRadius="6px"
          backgroundColor="#FAFAFA"
          maxHeight="400px"
          overflow="scroll"
          zIndex={999}
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
          style={{
            opacity: animate ? 1 : 0,
            transform: animate ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            paddingBottom: isScrollable ? '24px' : undefined,
          }}
          props={{ref: dropdownRef}}
        >
          {options.map((option) => (
            <Block
              key={option}
              padding="8px 12px"
              color="#333"
              cursor="pointer"
              hoverBackgroundColor="#F3F3F3"
              transition="background-color 0.2s ease"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              props={{
                onClick: () => handleSelect(option),
              }}
            >
              {option}
            </Block>
          ))}
        </Col>
      )}
    </Col>
  )
}

export default CustomDropdown
